import Alert from '@mui/material/Alert';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { Dropzone } from '@src/common/Dropzone';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import type { UploadFile } from '@src/documents/components/FormFileUpload';
import { FileItem } from '@src/documents/components/FormFileUpload';
import { useDeleteDocument } from '@src/documents/hooks/useDeleteDocument';
import { getLastPartOfPath } from '@src/documents/hooks/useFindAllDocumentsForOrder';
import { useMarkToSendDocument } from '@src/documents/hooks/useMarkToSendDocument';
import { useUploadStatePermit } from '@src/documents/hooks/useUploadStatePermit';
import type { PermitApplication } from '@src/orders/domain/PermitApplication';
import {
  type CompletePermitSchemaType,
  StatePermitDocumentFields,
} from '@src/permits/completePermit/fields/CompletePermitFields';
import { GenerateSelfIssuedPdfButton } from '@src/permits/completePermit/GenerateSelfIssuedPdfButton';
import { isAxiosError } from 'axios';
import { useDropzone } from 'react-dropzone';
import type { Control, UseFormGetValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';
import { type InferType, object } from 'yup';
import * as yup from 'yup';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StatePermitForm = object().shape({
  [StatePermitDocumentFields.fieldName]: object()
    .shape({
      name: yup.string().ensure().required(),
      documentId: yup.string().ensure().required(),
      sendToClient: yup.boolean().required(),
      displayName: yup.string().optional(),
      check: yup.boolean().optional(),
    })
    .optional(),
});
export type StatePermitFormType = InferType<typeof StatePermitForm>;

export const StatePermitDropzone = ({
  orderId,
  permitApplication,
  readOnly,
  control,
  setValue,
  getValues,
  isSelfIssued,
}: {
  orderId: string;
  permitApplication: PermitApplication;
  readOnly?: boolean;
  control: Control<CompletePermitSchemaType & StatePermitFormType>;
  setValue: UseFormSetValue<StatePermitFormType>;
  getValues: UseFormGetValues<CompletePermitSchemaType>;
  isSelfIssued?: boolean;
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [currentUploadFile] = useWatch({
    control,
    name: [StatePermitDocumentFields.fieldName],
  });

  const { uploadStatePermit } = useUploadStatePermit({
    orderId,
    permitApplicationId: permitApplication.id,
    onSuccess: (newDocument) => {
      const uploadFile = {
        documentId: newDocument.documentId,
        name: getLastPartOfPath(newDocument.key),
        sendToClient: newDocument.sendToClient,
        displayName: newDocument.displayName,
        check: newDocument.sendToClient,
      };
      setValue(StatePermitDocumentFields.fieldName, uploadFile, {
        shouldValidate: true,
      });
      showToast({
        severity: ToastLevel.success,
        message: t('fileUpload.success'),
      });
    },
    onError: (error) => {
      showToast({
        severity: ToastLevel.error,
        message: t(uploadErrorKey(error)),
      });
    },
  });
  const { deleteDocument } = useDeleteDocument({
    onSuccess: () => {
      setValue(StatePermitDocumentFields.fieldName, undefined, {
        shouldValidate: true,
      });
      showToast({
        severity: ToastLevel.success,
        message: t('fileDeletion.success'),
      });
    },
    onError: () => {
      showToast({
        severity: ToastLevel.error,
        message: t('fileDeletion.error'),
      });
    },
  });

  const { markToSend } = useMarkToSendDocument({ orderId });
  const onCheck = ({ checked }: { checked: boolean }) => {
    if (!currentUploadFile) {
      return;
    }
    markToSend({
      documentId: currentUploadFile.documentId,
      sendToClient: checked,
    });
    setValue(
      StatePermitDocumentFields.fieldName,
      { ...currentUploadFile, sendToClient: checked, check: checked },
      { shouldValidate: true },
    );
  };

  const onRename = ({ name }: { name?: string }) => {
    if (name && currentUploadFile) {
      setValue(
        StatePermitDocumentFields.fieldName,
        {
          documentId: currentUploadFile.documentId,
          name: currentUploadFile.name,
          sendToClient: currentUploadFile.sendToClient,
          displayName: name,
        },
        {
          shouldValidate: true,
        },
      );
    }
  };

  return (
    <SingleDocumentDropzone
      readOnly={readOnly}
      onDrop={(acceptedFiles) => {
        uploadStatePermit(acceptedFiles[0]);
      }}
      onRename={onRename}
      onDelete={() => {
        if (!currentUploadFile) {
          return;
        }
        deleteDocument({ documentId: currentUploadFile.documentId, index: 0 });
      }}
      onCheck={onCheck}
      currentDocument={currentUploadFile}
      control={control}
      additionalAction={
        <GenerateSelfIssuedPdfButton
          isSelfIssued={isSelfIssued}
          getValues={getValues}
          setValue={setValue}
          control={control}
          permitApplication={permitApplication}
        />
      }
    />
  );
};

const SingleDocumentDropzone = ({
  onDrop,
  onDelete,
  onRename,
  onCheck,
  currentDocument,
  readOnly,
  control,
  additionalAction,
}: {
  onDrop: (acceptedFiles: File[]) => void;
  onDelete: () => void;
  onRename: ({ name, id }: { name?: string; id?: string }) => void;
  onCheck: ({ checked }: { checked: boolean }) => void;
  currentDocument: UploadFile | undefined;
  readOnly?: boolean;
  control: Control<CompletePermitSchemaType & StatePermitFormType>;
  additionalAction?: JSX.Element;
}) => {
  const { t } = useTranslation();
  const showDropzone = !currentDocument && !readOnly;
  const showNoStatePermitBanner = !currentDocument && readOnly;
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
    },
    disabled: !!currentDocument || readOnly,
  });

  return (
    <Stack>
      {showDropzone && (
        <Dropzone
          isSmall
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          open={open}
          singleFile
          additionalAction={additionalAction}
        />
      )}
      {showNoStatePermitBanner && (
        <Alert severity="info">{t('noStatePermitUploaded')}</Alert>
      )}

      {currentDocument && (
        <Stack
          direction="column"
          gap={theme.spacing['3']}
          marginTop={theme.spacing['4']}
        >
          {!readOnly && (
            <Alert severity="warning">{t('sendFileToClientWarning')}</Alert>
          )}
          <FileItem
            onRename={onRename}
            documentFieldPath={StatePermitDocumentFields.fieldName}
            key={currentDocument.documentId}
            index={0}
            file={currentDocument}
            onDelete={onDelete}
            onCheck={onCheck}
            control={control}
            readOnly={readOnly}
            fieldValue={currentDocument.sendToClient}
          />
        </Stack>
      )}
    </Stack>
  );
};

const UPLOAD_ERROR_MESSAGE_KEYS: Record<string, string> = {
  EncryptedPdfException: 'fileUpload.encryptedPdf',
  UnreadablePdfException: 'fileUpload.unreadablePdf',
};

const uploadErrorKey = (error: Error) =>
  (isAxiosError<{ error: string }>(error) &&
    UPLOAD_ERROR_MESSAGE_KEYS[error.response?.data.error ?? '']) ||
  'fileUpload.error';
